import { Combobox } from "design-system/organisms/Combobox"
import { cn } from "design-system/utils"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomersResponseDataSchema } from "schema"
import { z } from "zod"

type Option =
  | (React.ComponentProps<typeof Combobox>["options"][number] & {
      data?: z.infer<typeof CustomersResponseDataSchema>[number]
    })
  | undefined

type CampaignsFormSelectorProps = {
  onChange: (value: Option) => void
  placeholder: string
  emptyPlaceholder: string
  searchPlaceholder: string
  className?: string
  avoidDisplaySelected?: boolean
  value?: string | null
}

const CampaignsFormSelector = ({
  onChange,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
  className,
  avoidDisplaySelected,
  value,
}: CampaignsFormSelectorProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })
  const [selectedValue, setSelectedValue] = useState<Option>(undefined)

  const { data: campaigns, isPending } = useCampaignsQuery({
    query: {
      query: debouncedSearchQuery,
      page: 1,
      total: 20,
      sortBy: { field: "name", order: "asc" },
      includeIds: value ? [value] : undefined,
    },
  })

  const options = useMemo(() => {
    if (!campaigns) return []

    return campaigns.map((campaign) => {
      return {
        label: campaign.name ?? t("campaigns.untitled"),
        value: campaign.id,
        data: campaign,
      }
    })
  }, [campaigns, t])

  function onComboboxChange(option: Option) {
    if (!avoidDisplaySelected) {
      setSelectedValue(option)
    }
    onChange(option)
  }

  useEffect(() => {
    if (value && campaigns) {
      const result = options.find((e) => e.value === value)

      if (result) {
        setSelectedValue({ label: result.label, value: result.value })
      }
    }
  }, [value, campaigns, options])

  return (
    <div className={cn("flex items-center space-x-4", className)}>
      <Combobox
        options={options}
        value={selectedValue}
        onChange={onComboboxChange}
        placeholder={placeholder}
        emptyPlaceholder={emptyPlaceholder}
        searchPlaceholder={searchPlaceholder}
        disabled={isPending}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        className="w-full"
      />
    </div>
  )
}

export default CampaignsFormSelector
